'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_RESET_DELAY_MS = 2000;

interface IUseCopyToClipboardOptions {
  /** How long the copied state stays set after a successful copy. */
  resetDelayMs?: number;
}

interface ICopied {
  text: string;
  key: string;
}

/**
/**
 * Writes text to the clipboard and remembers the last successful copy,
 * allowing the UI to display transient "Copied" feedback.
 *
 * The `copy` function returns a Promise that resolves to `true` on success,
 * and `false` if the Clipboard API is unavailable (e.g., insecure context or some webviews)
 * or if the write operation is rejected.
 *
 * After a successful copy:
 *   - `copiedText` holds the text placed on the clipboard
 *   - `copiedKey` identifies which copy target triggered the action
 *     (you can reuse the same hook for multiple copy targets inside a single component)
 *
 * Both values clear after `resetDelayMs`.
 * The key defaults to the copied text, but you can provide an explicit key
 * (for example, when using a single hook instance for several copy targets
 * in a list or table) so that only the clicked one shows its feedback.
 */
export const useCopyToClipboard = ({
  resetDelayMs = DEFAULT_RESET_DELAY_MS,
}: IUseCopyToClipboardOptions = {}) => {
  const [copied, setCopied] = useState<ICopied | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeout.current), []);

  const copy = useCallback(
    async (text: string, key: string = text): Promise<boolean> => {
      if (!navigator?.clipboard) {
        return false;
      }

      try {
        await navigator.clipboard.writeText(text);
      } catch {
        setCopied(null);
        return false;
      }

      clearTimeout(timeout.current);
      setCopied({ text, key });
      timeout.current = setTimeout(() => setCopied(null), resetDelayMs);
      return true;
    },
    [resetDelayMs],
  );

  return { copiedText: copied?.text ?? null, copiedKey: copied?.key ?? null, copy };
};
